import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Globe, Mail, Phone, Building, Tag, RefreshCw, Users
} from 'lucide-react';
import {
  listSuppliers, getSupplierStats, createSupplier, updateSupplier, deactivateSupplier, seedSuppliers,
  enrichSupplier
} from '../lib/api';
import IntelBadge from '../components/intel/IntelBadge';
import SupplierContactsDialog from '../components/suppliers/SupplierContactsDialog';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

const CATEGORIES = [
  { value: 'manufacturer', label_pl: 'Producent', label_en: 'Manufacturer' },
  { value: 'distributor',  label_pl: 'Dystrybutor', label_en: 'Distributor' },
  { value: 'wholesaler',   label_pl: 'Hurtownia', label_en: 'Wholesaler' },
  { value: 'specialty',    label_pl: 'Specjalistyczny', label_en: 'Specialty' },
  { value: 'other',        label_pl: 'Inny', label_en: 'Other' },
];

const TIER_LABEL = { 1: 'A (premium)', 2: 'B (preferred)', 3: 'C (standard)', 4: 'D (fallback)' };
const TIER_COLOR = { 1: 'bg-emerald-500/15 text-emerald-300', 2: 'bg-blue-500/15 text-blue-300', 3: 'bg-amber-500/15 text-amber-300', 4: 'bg-zinc-500/15 text-zinc-300' };

const EMPTY_FORM = {
  name: '', category: 'manufacturer', country: 'PL', tier: 3,
  email: '', phone: '', website: '', contact_person: '', nip: '', notes: '',
};

export default function Suppliers({ lang, tenantId }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contactsDialogSupplier, setContactsDialogSupplier] = useState(null);

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [search, filterCategory, filterCountry, filterTier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open contacts dialog jeśli URL ma ?contacts={supplier_id}
  // (toast z ProfitRoom 'Dodaj opiekuna' deep-link'uje tutaj)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contactsId = params.get('contacts');
    if (contactsId && items.length > 0) {
      const sup = items.find((s) => s.id === contactsId);
      if (sup) {
        setContactsDialogSupplier(sup);
        // Clean URL żeby nie reopen przy refresh
        const url = new URL(window.location);
        url.searchParams.delete('contacts');
        window.history.replaceState({}, '', url);
      }
    }
  }, [items]);

  async function loadData() {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        listSuppliers({ limit: 100 }).catch(() => ({ items: [] })),
        getSupplierStats().catch(() => null),
      ]);
      setItems(list.items || []);
      setStats(st);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function reload() {
    try {
      const params = { limit: 100 };
      if (search) params.q = search;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterCountry !== 'all') params.country = filterCountry;
      if (filterTier !== 'all') { params.tier_min = parseInt(filterTier); params.tier_max = parseInt(filterTier); }
      const list = await listSuppliers(params);
      setItems(list.items || []);
    } catch (err) { console.error(err); }
  }

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(s) {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      category: s.category || 'other',
      country: s.country || 'PL',
      tier: s.tier || 3,
      email: s.email || '',
      phone: s.phone || '',
      website: s.website || '',
      contact_person: s.contact_person || '',
      nip: s.nip || '',
      notes: s.notes || '',
    });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error(t(lang, 'Nazwa wymagana', 'Name required'));
      return;
    }
    try {
      if (editingId) {
        await updateSupplier(editingId, form);
        toast.success(t(lang, 'Zaktualizowano', 'Updated'));
      } else {
        await createSupplier(form);
        toast.success(t(lang, 'Dodano dostawcę', 'Supplier added'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(t(lang, 'Błąd zapisu', 'Save error'));
    }
  }

  async function deactivate(s) {
    if (!window.confirm(t(lang,
      `Dezaktywować "${s.name}"? Można przywrócić edytując pole active.`,
      `Deactivate "${s.name}"? Can be restored by editing active flag.`))) return;
    try {
      await deactivateSupplier(s.id);
      toast.success(t(lang, 'Zdezaktywowano', 'Deactivated'));
      loadData();
    } catch (err) { toast.error('Error'); }
  }

  async function reseed() {
    if (!window.confirm(t(lang,
      'Wgrać początkową bazę 80 lighting suppliers? (idempotentne — istniejące nie nadpisują)',
      'Seed initial 80 lighting suppliers? (idempotent — existing not overwritten)'))) return;
    try {
      const r = await seedSuppliers(false);
      toast.success(t(lang,
        `Dodano: ${r.inserted}, pominięto: ${r.skipped}`,
        `Inserted: ${r.inserted}, skipped: ${r.skipped}`));
      loadData();
    } catch (err) { toast.error('Error'); }
  }

  const visibleItems = items;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            {t(lang, 'Baza dostawców', 'Suppliers')}
          </h1>
          {stats && (
            <p className="text-xs text-muted-foreground mt-1">
              {stats.total} {t(lang, 'dostawców', 'suppliers')} ·{' '}
              {stats.by_country && Object.entries(stats.by_country).slice(0, 3).map(([c, n]) =>
                `${c}: ${n}`).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reseed} data-testid="suppliers-seed">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            {t(lang, 'Seed bazę 80', 'Seed base 80')}
          </Button>
          <Button onClick={openCreate} data-testid="suppliers-add">
            <Plus className="w-3.5 h-3.5 mr-1" />
            {t(lang, 'Dodaj dostawcę', 'Add supplier')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">{t(lang, 'Szukaj', 'Search')}</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t(lang, 'Nazwa, NIP, email...', 'Name, tax-id, email...')}
                className="pl-7"
                data-testid="suppliers-search"
              />
            </div>
          </div>
          <div className="min-w-[140px]">
            <Label className="text-xs">{t(lang, 'Kategoria', 'Category')}</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t(lang, 'Wszystkie', 'All')}</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>
                    {lang === 'pl' ? c.label_pl : c.label_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[100px]">
            <Label className="text-xs">{t(lang, 'Kraj', 'Country')}</Label>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t(lang, 'Wszystkie', 'All')}</SelectItem>
                <SelectItem value="PL">PL</SelectItem>
                <SelectItem value="DE">DE</SelectItem>
                <SelectItem value="IT">IT</SelectItem>
                <SelectItem value="CN">CN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[100px]">
            <Label className="text-xs">Tier</Label>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t(lang, 'Wszystkie', 'All')}</SelectItem>
                <SelectItem value="1">A (1)</SelectItem>
                <SelectItem value="2">B (2)</SelectItem>
                <SelectItem value="3">C (3)</SelectItem>
                <SelectItem value="4">D (4)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {t(lang, 'Ładowanie...', 'Loading...')}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {t(lang, 'Brak dostawców spełniających kryteria', 'No suppliers match filters')}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {visibleItems.map(s => (
                <div
                  key={s.id}
                  className={`p-3 flex items-center gap-3 hover:bg-muted/30 transition-colors ${
                    s.active === false ? 'opacity-50' : ''
                  }`}
                  data-testid={`supplier-row-${s.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{s.name}</span>
                      <Badge variant="outline" className="text-[10px]">{s.country || '—'}</Badge>
                      {s.tier && (
                        <Badge className={`text-[10px] ${TIER_COLOR[s.tier] || ''}`}>
                          {TIER_LABEL[s.tier] || `T${s.tier}`}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">{s.category}</Badge>
                      {s.active === false && (
                        <Badge className="text-[10px] bg-red-500/15 text-red-300">
                          {t(lang, 'nieaktywny', 'inactive')}
                        </Badge>
                      )}
                      <IntelBadge
                        intel={s.intel_summary}
                        onEnrich={() => enrichSupplier(s.id).then((r) => { loadData(); return r; })}
                        lang={lang}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5 flex-wrap">
                      {s.contact_person && <span>{s.contact_person}</span>}
                      {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>}
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                      {s.website && (
                        <a href={s.website} target="_blank" rel="noreferrer"
                           className="flex items-center gap-1 hover:text-primary">
                          <Globe className="w-3 h-3" />{s.website.replace(/^https?:\/\//, '').slice(0, 30)}
                        </a>
                      )}
                      {s.nip && <span><Tag className="w-3 h-3 inline mr-1" />NIP: {s.nip}</span>}
                      {s.response_rate_pct != null && (
                        <span className="text-emerald-400">
                          ↩ {s.response_rate_pct}% {t(lang, 'odpowiedzi', 'response')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setContactsDialogSupplier(s)}
                      title={t(lang, 'Moi opiekunowie + auto-discovery', 'My contacts + auto-discovery')}
                      data-testid={`supplier-contacts-${s.id}`}
                    >
                      <Users className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)}
                            data-testid={`supplier-edit-${s.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                            onClick={() => deactivate(s)}
                            data-testid={`supplier-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t(lang, 'Edytuj dostawcę', 'Edit supplier')
                : t(lang, 'Nowy dostawca', 'New supplier')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t(lang, 'Nazwa', 'Name')} *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t(lang, 'Kategoria', 'Category')}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {lang === 'pl' ? c.label_pl : c.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tier</Label>
                <Select value={String(form.tier)} onValueChange={v => setForm({ ...form, tier: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map(n => (
                      <SelectItem key={n} value={String(n)}>{TIER_LABEL[n]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t(lang, 'Kraj', 'Country')}</Label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">NIP</Label>
                <Input value={form.nip} onChange={e => setForm({ ...form, nip: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t(lang, 'Osoba kontaktowa', 'Contact person')}</Label>
              <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t(lang, 'Telefon', 'Phone')}</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </div>
            <div>
              <Label className="text-xs">{t(lang, 'Notatki', 'Notes')}</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t(lang, 'Anuluj', 'Cancel')}
            </Button>
            <Button onClick={save} data-testid="supplier-save">
              {editingId ? t(lang, 'Zapisz', 'Save') : t(lang, 'Dodaj', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contacts (Moi opiekunowie) dialog */}
      <SupplierContactsDialog
        open={!!contactsDialogSupplier}
        onClose={() => setContactsDialogSupplier(null)}
        supplier={contactsDialogSupplier}
        lang={lang}
      />
    </div>
  );
}
